import React from 'react';

import {
  TpHoverInfo,
  TpHoverRenderContext,
  TpHoverRenderInfo,
  ZoomPanCanvas,
} from '../../zoompan-canvas/ZoomPanCanvas';
import { TpZoomPanelInvPointMapInfo } from '../../zoompan-canvas/zoomPanCanvasHelpers';
import { TpChartProps } from '../interfaces';
import { createDefinition } from './network.Renderer';
import { useAppDispatch } from '../../../../app/hooks';
import { color } from '../../helpers/colors';
import { TpChartColorSchema, TpLassoSelection, getPointsInsideLassoSelection } from '../../zoompan-canvas/helpers';
import { TpEnSelectionFilterTypes } from '../../../../data/selection/interfaces';
import { performUserSelectionAction } from '../../../../data/analyticsDataSlice';
import { convertColumnDataToDisplayString } from '../../helpers/misc';
import { DASHBOARD_ACTION_TRIGGERIDS } from '../../actions/executeActionTriggers';
import { sendEvent } from '../../hooks/events';

export function Network({ chartTypeManifest, dataContext, dataBinding, config, widgetContext }: TpChartProps) {
  const dispatch = useAppDispatch();

  const colorSchema: TpChartColorSchema = {
    backColor: color(0, 0, 0),
    foreColor: color(255, 255, 255),
  };

  const widgetGlobalConfig = widgetContext.dashboardContext.widgetGlobalConfig ?? {};
  const {
    definition,
    areaPoints,
    dataTable,
    dataX,
    dataY,
    dataItemId,
    dataHoverLabel,
    pointCount,
    nameX,
    nameY,
    colInfoX,
    colInfoY,
    colInfoHover,
    getItemIndexById,
    edgesInfo,
    customHoverRenderer,
    getExpandedSelection,
    title,
  } = createDefinition(chartTypeManifest, dataContext, dataBinding, config, colorSchema, widgetGlobalConfig);

  const backColor = color(0, 0, 0);

  function getHoverInfo(
    mappingInfo: TpZoomPanelInvPointMapInfo,
    logicPositX: number,
    logicPositY: number,
    renderPositX: number,
    renderPositY: number,
  ): TpHoverInfo | null {
    if (mappingInfo.area?.id != 'points') return null;
    let minDist = mappingInfo.mapsize(10);
    let selItemId = null;
    for (let pointNr = 0; pointNr < pointCount; pointNr++) {
      const scr_x = mappingInfo.mapX.map(dataX[pointNr]);
      const scr_y = mappingInfo.mapY.map(dataY[pointNr]);
      const dst = Math.abs(scr_x - renderPositX) + Math.abs(scr_y - renderPositY);
      if (dst <= minDist) {
        selItemId = dataItemId[pointNr];
        minDist = dst;
      }
    }
    if (selItemId == null) return null;
    return {
      dataTableId: dataBinding.dataTableId,
      itemId: selItemId,
    };
  }

  function getHoverRenderInfo(hoverInfo: TpHoverInfo, context: TpHoverRenderContext): TpHoverRenderInfo | null {
    if (hoverInfo.dataTableId != dataBinding.dataTableId || hoverInfo.catColumnId) return null;
    const hoverItemNr = getItemIndexById(hoverInfo.itemId);
    if (hoverItemNr === undefined) return null;
    const labels: string[] = [];
    if (context.isActiveChart) {
      if (dataHoverLabel) labels.push(convertColumnDataToDisplayString(colInfoHover, dataHoverLabel[hoverItemNr]));
      const links = edgesInfo.nodeLinks[dataItemId[hoverItemNr]];
      labels.push(`Nr of links: ${links.length}`);
    }
    return {
      area: areaPoints,
      logicPosX: dataX[hoverItemNr],
      logicPosY: dataY[hoverItemNr],
      labels,
      customHoverRenderer,
    };
  }

  function onLassoSelection(lassoSelection: TpLassoSelection) {
    const points: { x: number; y: number; id: any }[] = [];
    for (let pointNr = 0; pointNr < pointCount; pointNr++) {
      const x = dataX[pointNr];
      const y = dataY[pointNr];
      if (!isNaN(x) && !isNaN(y)) points.push({ x, y, id: dataItemId[pointNr] });
    }
    const selectedPoints = getPointsInsideLassoSelection(lassoSelection, points);
    widgetContext.handleUserSelectionAction({
      type: TpEnSelectionFilterTypes.LASSO2D,
      dataTableId: dataTable.id,
      colIdX: colInfoX.id,
      colIdY: colInfoY.id,
      selectedItemIds: selectedPoints.map((pt) => pt.id),
      lassoPoints: lassoSelection.logicPoints,
    });
  }

  function onItemClicked(itemId: string) {
    widgetContext.handleOpenItem(dataTable.canonicalTypeId, itemId);
  }

  function expandSelection() {
    // Expands the current selection of nodes by adding all direct links to that selection
    const expandedSelection = getExpandedSelection!();
    dispatch(
      performUserSelectionAction({
        dataContextId: dataContext.id,
        canonicalTypeId: dataTable.canonicalTypeId,
        userSelectionAction: {
          type: TpEnSelectionFilterTypes.MANUAL,
          description: 'Expanded network selection',
          method: 'replace',
          selectedItemIds: expandedSelection,
        },
      }),
    );
    sendEvent(DASHBOARD_ACTION_TRIGGERIDS.ENTITY_SELECTION_MODIFIED_BY_USER, {
      dataContextId: dataContext.id,
      canonicalTypeId: dataTable.canonicalTypeId,
    });
  }

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <ZoomPanCanvas
        definition={definition}
        getHoverInfo={getHoverInfo}
        getHoverRenderInfo={getHoverRenderInfo}
        onLassoSelection={onLassoSelection}
        onItemClicked={onItemClicked}
        maxZoomXRelative={30}
        maxZoomYRelative={30}
        widgetContext={widgetContext}
        colorSchema={colorSchema}
        chartTitleInfo={{
          assetId: dataContext.assetId,
          dataType: chartTypeManifest.id,
          title,
        }}
      />
      {getExpandedSelection && ( // in case we can expand a curent selection of nodes, show a button to do this
        <div
          style={{
            // @todo: replace this temporay styling by styling class
            position: 'absolute',
            top: '55px', // same level as the top of the menu and below hover info
            right: '10px',
            width: '30px',
            height: '30px',
            backgroundColor: 'rgba(128,128,128,0.5)',
            color: 'rgb(255,255,255)',
            cursor: 'pointer',
            textAlign: 'center',
            lineHeight: '30px',
            fontSize: '130%',
          }}
          onClick={expandSelection}
        >
          +
        </div>
      )}
    </div>
  );
}
